/**
 * LEGACY end-of-run item loot and the item vault (PLAN §11 "Loot"), kept for tools and old
 * tests. Since save v3 runs pay resources instead (payout.rollRunResources, crafting.md §2); only
 * `rollRunGems` and `trackVault` are still used by applyRunResult. Deterministic given the Rng.
 * The order of RNG draws is part of the contract (documented on each function): change it
 * only with a save-version bump if loot replays ever matter.
 */
import type { ClassId, Rng, Role } from '@keystone/sim';
import { CLASS_STAT_PROFILES, MAX_GEM_RANK } from './data';
import { BASE_ILVL, generateItem, makeItem } from './items';
import { legendariesFor } from './items.legendaries';
import { tierDef } from './rating';
import type { Gem, Item, ItemId, ItemSlot, LegendaryId, LootContext, RankTier, RunLoot, SecondaryStat, VaultState } from './types';
import { GEM_COLORS } from './types';

export const VAULT_EVERY = 5;
export const VAULT_CHOICES = 3;
/** Vault ilvl bonus over a timed run drop of the best key in the window. */
export const VAULT_ILVL_BONUS = 5;
/** Item levels gained per key level above +2. */
export const ILVL_PER_KEY_LEVEL = 5;
/** Untimed runs drop this many item levels lower. */
export const UNTIMED_ILVL_PENALTY = 5;
/** Bonus-roll chance on a timed run: base + per chest (+1/+2/+3). */
export const BONUS_ROLL_BASE = 0.2;
export const BONUS_ROLL_PER_CHEST = 0.1;
/** Chance a dropped gem is one rank above the key's base gem rank. */
export const GEM_RANK_UP_CHANCE = 0.15;
/** Key levels per gem rank step (+2..+5 → rank 1, +6..+9 → rank 2, ...). */
export const KEY_LEVELS_PER_GEM_RANK = 4;

/** Item level of a run drop: +5 per key level above +2, capped by the tier. Untimed −5. */
export function dropIlvl(level: number, timed: boolean, tier: RankTier): number {
  const raw = BASE_ILVL + ILVL_PER_KEY_LEVEL * Math.max(0, level - 2) - (timed ? 0 : UNTIMED_ILVL_PENALTY);
  return Math.max(BASE_ILVL, Math.min(tierDef(tier).maxIlvl, raw));
}

/** Uncapped drop ilvl for a key (the UI shows "your tier caps this at N"). */
export function uncappedDropIlvl(level: number, timed: boolean): number {
  return Math.max(BASE_ILVL, BASE_ILVL + ILVL_PER_KEY_LEVEL * Math.max(0, level - 2) - (timed ? 0 : UNTIMED_ILVL_PENALTY));
}

/** Shards from a run. Only timed runs pay Shards (PLAN §11). */
export function shardsForRun(level: number, timed: boolean, chest: 0 | 1 | 2 | 3): number {
  return timed ? 10 + 2 * level + 5 * chest : 0;
}

/** Base rank of a gem dropped from a key of this level, before the tier cap. */
export function gemRankForKey(level: number): number {
  return Math.max(1, 1 + Math.floor((level - 2) / KEY_LEVELS_PER_GEM_RANK));
}

export interface LootOptions {
  /** Gems are unlocked (Contender 3+, `hasUnlock(rating, 'gems')`). Default: the tier allows any gem rank. */
  gemsUnlocked?: boolean;
  /** Legendaries the profile already owns: a legendary roll prefers ones not owned. */
  ownedLegendaries?: readonly LegendaryId[];
}

export interface RollOneSpec {
  classId: ClassId;
  role?: Role;
  ilvl: number;
  tier: RankTier;
  nextId: () => ItemId;
  source: Item['source'];
  excludeSlots?: readonly ItemSlot[];
  ownedLegendaries?: readonly LegendaryId[];
}

/**
 * One item for one recipient. Draw order:
 *  1. legendary chance (only if the tier allows and the class has an eligible legendary)
 *  2a. legendary: pick, then 2 secondary draws (sockets fixed at 1)
 *  2b. otherwise `generateItem` (see items.ts for its order)
 */
export function rollItem(spec: RollOneSpec, rng: Rng): Item {
  const legChance = tierDef(spec.tier).legendaryChance;
  const all = legendariesFor(spec.classId).filter((l) => !spec.excludeSlots?.includes(l.slot));
  if (legChance > 0 && all.length > 0 && rng.chance(legChance)) {
    const fresh = all.filter((l) => !spec.ownedLegendaries?.includes(l.id));
    const leg = rng.pick(fresh.length ? fresh : all)!;
    return makeItem({ id: spec.nextId(), slot: leg.slot, ilvl: spec.ilvl, legendaryId: leg.id, source: spec.source, ...priorityOf(spec.classId) }, rng);
  }
  return generateItem(
    {
      id: spec.nextId(),
      ilvl: spec.ilvl,
      classId: spec.classId,
      source: spec.source ?? 'run',
      ...(spec.role ? { role: spec.role } : {}),
      ...(spec.excludeSlots ? { excludeSlots: spec.excludeSlots } : {}),
    },
    rng,
  );
}

function priorityOf(classId: ClassId): { priority?: readonly SecondaryStat[] } {
  const priority = CLASS_STAT_PROFILES[classId]?.priority;
  return priority ? { priority } : {};
}

/**
 * Loot for a finished run. Everyone gets 1 item (bots included); recipient 0 is the human and
 * gets the bonus roll. Draw order:
 *  1. one `rollItem` per recipient, in order
 *  2. timed only: bonus-roll chance, then `rollItem` for recipient 0 on a hit
 *  3. timed + gems unlocked only: per recipient, colour pick then rank-up chance
 * `gems[i]` belongs to recipient i (the profile sockets bot gems into the bot's gear).
 */
export function rollRunLoot(ctx: LootContext, rng: Rng, opts: LootOptions = {}): RunLoot {
  const ilvl = dropIlvl(ctx.level, ctx.timed, ctx.tier);
  const owned = [...(opts.ownedLegendaries ?? [])];
  const roll = (classId: ClassId, role: Role, source: Item['source']): Item => {
    const it = rollItem({ classId, role, ilvl, tier: ctx.tier, nextId: ctx.nextId, source, ownedLegendaries: owned }, rng);
    if (it.legendaryId) owned.push(it.legendaryId);
    return it;
  };
  const items = ctx.recipients.map((r) => roll(r.classId, r.role, 'run'));
  const human = ctx.recipients[0];
  let bonus: Item | null = null;
  if (ctx.timed && human && rng.chance(BONUS_ROLL_BASE + BONUS_ROLL_PER_CHEST * ctx.chest)) bonus = roll(human.classId, human.role, 'bonus');

  const gems = rollRunGems(ctx.level, ctx.timed, ctx.tier, ctx.recipients.length, opts.gemsUnlocked !== false, rng);
  return { items, bonus, shards: shardsForRun(ctx.level, ctx.timed, ctx.chest), gems };
}

/**
 * End-of-run gems (unchanged by the Altar Forge): timed runs with gems unlocked drop one gem per
 * recipient. Draw order per recipient: colour pick, then rank-up chance.
 */
export function rollRunGems(level: number, timed: boolean, tier: RankTier, recipients: number, gemsUnlocked: boolean, rng: Rng): Gem[] {
  const gems: Gem[] = [];
  const maxRank = Math.min(MAX_GEM_RANK, tierDef(tier).maxGemRank);
  if (!timed || maxRank <= 0 || !gemsUnlocked) return gems;
  const base = Math.min(maxRank, gemRankForKey(level));
  for (let i = 0; i < recipients; i++) {
    const color = rng.pick(GEM_COLORS)!;
    const rank = Math.min(maxRank, base + (rng.chance(GEM_RANK_UP_CHANCE) ? 1 : 0));
    gems.push({ color, rank });
  }
  return gems;
}

/**
 * Count a run toward the vault. Untimed runs count one key level lower. Returns the new state;
 * `ready` when a choice should be rolled from `windowLevels` (the completed window).
 */
export function trackVault(v: VaultState, level: number, timed = true): { vault: VaultState; ready: boolean; windowLevels: number[] } {
  const windowLevels = [...v.windowLevels, timed ? level : Math.max(2, level - 1)];
  const runsInWindow = v.runsInWindow + 1;
  if (runsInWindow >= VAULT_EVERY) return { vault: { ...v, runsInWindow: 0, windowLevels: [] }, ready: true, windowLevels };
  return { vault: { ...v, runsInWindow, windowLevels }, ready: false, windowLevels };
}

/** Item level of vault choices: a timed drop of the best key in the window, +5, capped by tier. */
export function vaultIlvl(windowLevels: readonly number[], tier: RankTier): number {
  const best = windowLevels.length ? Math.max(...windowLevels) : 2;
  return Math.min(tierDef(tier).maxIlvl, dropIlvl(best, true, tier) + VAULT_ILVL_BONUS);
}

/**
 * VAULT_CHOICES items in DIFFERENT slots at `vaultIlvl` (pick 1). Draw order: `rollItem` per
 * choice, each excluding the slots already offered.
 */
export function rollVault(
  windowLevels: readonly number[],
  classId: ClassId,
  tier: RankTier,
  nextId: () => ItemId,
  rng: Rng,
  opts: { role?: Role; ownedLegendaries?: readonly LegendaryId[] } = {},
): Item[] {
  const ilvl = vaultIlvl(windowLevels, tier);
  const out: Item[] = [];
  const owned = [...(opts.ownedLegendaries ?? [])];
  for (let i = 0; i < VAULT_CHOICES; i++) {
    const it = rollItem(
      { classId, ilvl, tier, nextId, source: 'vault', excludeSlots: out.map((o) => o.slot), ownedLegendaries: owned, ...(opts.role ? { role: opts.role } : {}) },
      rng,
    );
    if (it.legendaryId) owned.push(it.legendaryId);
    out.push(it);
  }
  return out;
}
